<template>
  <div class="contact-form-container">
    <div class="form-header">
      <h2>{{ $t('contactForm.title') }}</h2>
      <p>{{ $t('contactForm.desc') }}</p>
    </div>
    
    <el-form 
      ref="formRef" 
      :model="formData" 
      :rules="formRules" 
      label-position="top" 
      class="contact-form"
      @submit.prevent="submitForm"
    >
      <el-row :gutter="20">
        <el-col :span="12" :xs="24">
          <el-form-item :label="$t('contactForm.nameLabel')" prop="name">
            <el-input
              v-model="formData.name"
              :placeholder="$t('contactForm.namePlaceholder')"
              prefix-icon="el-icon-user"
            />
          </el-form-item>
        </el-col>

        <el-col :span="12" :xs="24">
          <el-form-item :label="$t('contactForm.emailLabel')" prop="email">
            <el-input
              v-model="formData.email"
              :placeholder="$t('contactForm.emailPlaceholder')"
              prefix-icon="el-icon-message"
              type="email"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12" :xs="24">
          <el-form-item :label="$t('contactForm.phoneLabel')" prop="phone">
            <el-input
              v-model="formData.phone"
              :placeholder="$t('contactForm.phonePlaceholder')"
              prefix-icon="el-icon-phone"
            />
          </el-form-item>
        </el-col>

        <el-col :span="12" :xs="24">
          <el-form-item :label="$t('contactForm.subjectLabel')" prop="subject">
            <el-select
              v-model="formData.subject"
              :placeholder="$t('contactForm.subjectPlaceholder')"
              style="width: 100%"
            >
              <!-- value 保持中文以兼容后端既有约定，仅展示文案随语言切换 -->
              <el-option :label="$t('contactForm.subjectProduct')" value="产品咨询" />
              <el-option :label="$t('contactForm.subjectTech')" value="技术支持" />
              <el-option :label="$t('contactForm.subjectCoop')" value="合作洽谈" />
              <el-option :label="$t('contactForm.subjectComplaint')" value="投诉建议" />
              <el-option :label="$t('contactForm.subjectOther')" value="其他" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item :label="$t('contactForm.messageLabel')" prop="message">
        <el-input
          v-model="formData.message"
          type="textarea"
          rows="6"
          :placeholder="$t('contactForm.messagePlaceholder')"
        />
      </el-form-item>

      <el-form-item class="form-footer">
        <el-button
          type="primary"
          native-type="submit"
          :loading="submitLoading"
          size="large"
          class="submit-btn"
        >
          {{ $t('contactForm.submit') }}
        </el-button>
      </el-form-item>
    </el-form>
    
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="30%"
      center
    >
      <div class="dialog-content">
        <i :class="dialogIcon" class="dialog-icon"></i>
        <p>{{ dialogMessage }}</p>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button type="primary" @click="dialogVisible = false">{{ $t('contactForm.confirm') }}</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { contactApi } from '@/api';

export default {
  name: 'ContactForm',
  props: {
    successCallback: {
      type: Function,
      default: null
    }
  },
  setup(props) {
    const { t } = useI18n();

    // 表单引用
    const formRef = ref(null);

    // 表单数据
    const formData = reactive({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });

    // 表单验证规则（computed 保证切换语言后提示文案同步更新）
    const formRules = computed(() => ({
      name: [
        { required: true, message: t('contactForm.nameRequired'), trigger: 'blur' },
        { min: 2, max: 20, message: t('contactForm.nameLength'), trigger: 'blur' }
      ],
      email: [
        { required: true, message: t('contactForm.emailRequired'), trigger: 'blur' },
        { type: 'email', message: t('contactForm.emailFormat'), trigger: 'blur' }
      ],
      phone: [
        { required: true, message: t('contactForm.phoneRequired'), trigger: 'blur' },
        { pattern: /^1[3-9]\d{9}$/, message: t('contactForm.phoneFormat'), trigger: 'blur' }
      ],
      subject: [
        { required: true, message: t('contactForm.subjectRequired'), trigger: 'change' }
      ],
      message: [
        { required: true, message: t('contactForm.messageRequired'), trigger: 'blur' },
        { min: 10, max: 500, message: t('contactForm.messageLength'), trigger: 'blur' }
      ]
    }));
    
    // 提交状态
    const submitLoading = ref(false);
    
    // 对话框状态
    const dialogVisible = ref(false);
    const dialogTitle = ref('');
    const dialogMessage = ref('');
    const dialogIcon = ref('');
    
    // 表单提交
    const submitForm = () => {
      formRef.value.validate(async (valid) => {
        if (!valid) return;
        
        submitLoading.value = true;
        
        try {
          const res = await contactApi.submitContact(formData);
          
          if (res.data.ActionType === 'ok') {
            // 提交成功
            dialogTitle.value = t('contactForm.successTitle');
            dialogMessage.value = t('contactForm.successMessage');
            dialogIcon.value = 'el-icon-success success-icon';
            dialogVisible.value = true;

            // 重置表单
            formRef.value.resetFields();

            // 回调函数
            if (props.successCallback) {
              props.successCallback();
            }
          } else {
            // 提交失败
            throw new Error(res.data.message || t('contactForm.failTitle'));
          }
        } catch (error) {
          console.error('提交留言失败:', error);
          dialogTitle.value = t('contactForm.failTitle');
          dialogMessage.value = t('contactForm.failMessage');
          dialogIcon.value = 'el-icon-error error-icon';
          dialogVisible.value = true;
        } finally {
          submitLoading.value = false;
        }
      });
    };
    
    return {
      formRef,
      formData,
      formRules,
      submitLoading,
      dialogVisible,
      dialogTitle,
      dialogMessage,
      dialogIcon,
      submitForm
    };
  }
};
</script>

<style lang="scss" scoped>
.contact-form-container {
  max-width: 800px;
  margin: 0 auto;
  
  .form-header {
    text-align: center;
    margin-bottom: 40px;
    
    h2 {
      font-size: 32px;
      font-weight: 700;
      color: var(--secondary-color);
      margin-bottom: 15px;
    }
    
    p {
      color: var(--text-light-color);
      font-size: 16px;
      max-width: 600px;
      margin: 0 auto;
    }
  }
  
  .contact-form {
    padding: 40px;
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
    
    @media (max-width: 768px) {
      padding: 25px 20px;
    }
    
    :deep(.el-form-item__label) {
      font-weight: 600;
      color: var(--secondary-color);
    }
    
    :deep(.el-input__inner) {
      padding-left: 40px;
    }
    
    :deep(.el-input__prefix) {
      left: 10px;
      color: var(--primary-color);
    }
    
    .form-footer {
      margin-top: 20px;
      text-align: center;
      
      .submit-btn {
        padding: 12px 40px;
        font-size: 16px;
        transition: all 0.3s;
        
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(64, 158, 255, 0.3);
        }
      }
    }
  }
  
  .dialog-content {
    text-align: center;
    padding: 20px 0;
    
    .dialog-icon {
      font-size: 48px;
      margin-bottom: 15px;
      
      &.success-icon {
        color: #67c23a;
      }
      
      &.error-icon {
        color: #f56c6c;
      }
    }
    
    p {
      font-size: 16px;
      color: var(--text-color);
    }
  }
  
  .dialog-footer {
    text-align: center;
  }
}
</style> 